import {
  User,
  LoginCredentials,
  AuthResponse,
  ApiResponse,
  Branch
} from '../../types/api'
import { apiClient, setAuthToken, clearAuthToken } from '../../utils/api'

export class AuthService {
  private readonly basePath = '/auth'

  // Authentication
  async login(credentials: LoginCredentials): Promise<ApiResponse<AuthResponse>> {
    const response = await apiClient.post<AuthResponse>(`${this.basePath}/login`, credentials)
    
    if (response.data?.token) {
      setAuthToken(response.data.token)
    }
    
    return response
  }

  async logout(): Promise<ApiResponse<void>> {
    try {
      await apiClient.post(`${this.basePath}/logout`)
    } finally {
      clearAuthToken()
    }
    
    return { success: true, message: 'Logged out successfully', data: undefined }
  }

  async refreshToken(): Promise<ApiResponse<AuthResponse>> {
    const response = await apiClient.post<AuthResponse>(`${this.basePath}/refresh`)
    
    if (response.data?.token) {
      setAuthToken(response.data.token)
    }
    
    return response
  }

  async checkToken(): Promise<ApiResponse<{ valid: boolean, expires_at?: string }>> {
    return apiClient.get(`${this.basePath}/check-token`)
  }

  // User Profile
  async getProfile(): Promise<ApiResponse<User>> {
    return apiClient.get(`${this.basePath}/profile`)
  }

  async updateProfile(data: Partial<User>): Promise<ApiResponse<User>> {
    return apiClient.put(`${this.basePath}/profile`, data)
  }

  async changePassword(data: {
    current_password: string
    new_password: string
    new_password_confirmation: string
  }): Promise<ApiResponse<void>> {
    return apiClient.post(`${this.basePath}/change-password`, data)
  }

  // Password Reset
  async forgotPassword(email: string): Promise<ApiResponse<{ message: string }>> {
    return apiClient.post(`${this.basePath}/forgot-password`, { email })
  }

  async resetPassword(data: {
    token: string
    email: string
    password: string
    password_confirmation: string
  }): Promise<ApiResponse<{ message: string }>> {
    return apiClient.post(`${this.basePath}/reset-password`, data)
  }

  // Branch Management
  async getBranches(): Promise<ApiResponse<Branch[]>> {
    return apiClient.get(`${this.basePath}/branches`)
  }

  async setActiveBranch(branchId: string): Promise<ApiResponse<User>> {
    return apiClient.post(`${this.basePath}/set-active-branch`, { branch_id: branchId })
  }

  async getActiveBranch(): Promise<ApiResponse<Branch>> {
    return apiClient.get(`${this.basePath}/active-branch`)
  }

  // Permissions
  async getPermissions(): Promise<ApiResponse<string[]>> {
    return apiClient.get(`${this.basePath}/permissions`)
  }

  async hasPermission(permission: string): Promise<ApiResponse<{ has_permission: boolean }>> {
    return apiClient.get(`${this.basePath}/has-permission`, { permission })
  }

  async hasAnyPermission(permissions: string[]): Promise<ApiResponse<{ has_permission: boolean }>> {
    return apiClient.get(`${this.basePath}/has-any-permission`, { permissions })
  }

  // Session Management
  async getActiveSessions(): Promise<ApiResponse<Array<{
    id: string
    ip_address: string
    user_agent: string
    last_activity: string
    is_current: boolean
  }>>> {
    return apiClient.get(`${this.basePath}/sessions`)
  }

  async revokeSession(sessionId: string): Promise<ApiResponse<void>> {
    return apiClient.delete(`${this.basePath}/sessions/${sessionId}`)
  }

  async revokeAllOtherSessions(): Promise<ApiResponse<void>> {
    return apiClient.delete(`${this.basePath}/sessions/others`)
  }

  // Two-Factor Authentication
  async enableTwoFactor(): Promise<ApiResponse<{
    qr_code: string
    secret_key: string
    recovery_codes: string[]
  }>> {
    return apiClient.post(`${this.basePath}/two-factor/enable`)
  }

  async confirmTwoFactor(code: string): Promise<ApiResponse<{
    recovery_codes: string[]
  }>> {
    return apiClient.post(`${this.basePath}/two-factor/confirm`, { code })
  }

  async disableTwoFactor(password: string): Promise<ApiResponse<void>> {
    return apiClient.delete(`${this.basePath}/two-factor/disable`, { password })
  }

  async getTwoFactorRecoveryCodes(): Promise<ApiResponse<{ recovery_codes: string[] }>> {
    return apiClient.get(`${this.basePath}/two-factor/recovery-codes`)
  }

  async regenerateTwoFactorRecoveryCodes(): Promise<ApiResponse<{ recovery_codes: string[] }>> {
    return apiClient.post(`${this.basePath}/two-factor/recovery-codes`)
  }

  // Activity Logging
  async getActivityLog(filters?: {
    action?: string
    date_from?: string
    date_to?: string
    limit?: number
  }): Promise<ApiResponse<Array<{
    id: string
    action: string
    description: string
    ip_address: string
    user_agent: string
    created_at: string
  }>>> {
    return apiClient.get(`${this.basePath}/activity-log`, filters)
  }

  // Preferences
  async getPreferences(): Promise<ApiResponse<Record<string, any>>> {
    return apiClient.get(`${this.basePath}/preferences`)
  }

  async updatePreferences(preferences: Record<string, any>): Promise<ApiResponse<Record<string, any>>> {
    return apiClient.put(`${this.basePath}/preferences`, { preferences })
  }

  async setPreference(key: string, value: any): Promise<ApiResponse<void>> {
    return apiClient.post(`${this.basePath}/preferences/${key}`, { value })
  }

  // Account Verification
  async sendEmailVerification(): Promise<ApiResponse<{ message: string }>> {
    return apiClient.post(`${this.basePath}/email/verification-notification`)
  }

  async verifyEmail(data: {
    id: string
    hash: string
    expires: string
    signature: string
  }): Promise<ApiResponse<{ message: string }>> {
    return apiClient.get(`${this.basePath}/email/verify/${data.id}/${data.hash}`, {
      expires: data.expires,
      signature: data.signature
    })
  }

  // Device Management
  async getDevices(): Promise<ApiResponse<Array<{
    id: string
    name: string
    type: 'mobile' | 'tablet' | 'desktop'
    os: string
    browser: string
    last_used: string
    is_current: boolean
  }>>> {
    return apiClient.get(`${this.basePath}/devices`)
  }

  async revokeDevice(deviceId: string): Promise<ApiResponse<void>> {
    return apiClient.delete(`${this.basePath}/devices/${deviceId}`)
  }

  async updateDeviceName(deviceId: string, name: string): Promise<ApiResponse<void>> {
    return apiClient.put(`${this.basePath}/devices/${deviceId}`, { name })
  }
}

// Create and export service instance
export const authService = new AuthService() 